import { Box, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import React, { useEffect, useState } from 'react';
import AptaTable from 'examples/AptaTable';
import MDTypography from 'components/MDTypography';
import axios from 'axios';
import { URL } from 'constants/constant';
import Swal from 'sweetalert2';
import { SearchInput } from 'layouts/speakers';
import MilitaryTechOutlinedIcon from '@mui/icons-material/MilitaryTechOutlined';

const Results = () => {
  const [results, setResults] = useState([]);
  const [search, setSearch] = useState('');
  const [dayFilter, setDayFilter] = useState('All Days');
  const [filteredResults, setFilteredResults] = useState([]);

  const [allDays, setAllDays] = useState([]);
  const [overallToppers, setOverallToppers] = useState([]);
  const [filteredToppers, setFilteredToppers] = useState([]);
  const [topperTableMode, setTopperTableMode] = useState('overall');
  const [topperDayFilter, setTopperDayFilter] = useState('All Days');

  const parseTime = (time) => {
    if (!time) return Infinity;
    const parts = time.split(':').map(Number);
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    return Infinity;
  };

  const sortByPerformance = (a, b) => {
    if (b.correctAnswers !== a.correctAnswers)
      return b.correctAnswers - a.correctAnswers;
    return parseTime(a.timeTaken) - parseTime(b.timeTaken);
  };

  const getResults = async () => {
    try {
      const response = await axios.get(`${URL}/api/admin/quiz/overall-user`);
      if (response.data) {
        const sortedData = [...response.data.data].sort(sortByPerformance);
        setResults(sortedData);
      }
    } catch (err) {
      console.error("Error fetching overall quiz results:", err);
      Swal.fire("Error!", "Failed to fetch overall quiz results.", "error");
    }
  };

  const getAllDays = async () => {
    try {
      const response = await axios.get(`${URL}/api/admin/day`);
      if (response.data) setAllDays(response.data.data);
    } catch (err) {
      Swal.fire("Error!", "Failed to fetch day details.", "error");
    }
  };

  const getOverallToppers = async () => {
    try {
      const response = await axios.get(`${URL}/api/admin/quiz/overall-top-user`);
      if (response.data) setOverallToppers(response.data.data);
    } catch (err) {
      Swal.fire("Error!", "Failed to fetch overall toppers.", "error");
    }
  };

  useEffect(() => {
    getResults();
    getAllDays();
    getOverallToppers();
  }, []);

  // Filter for "Overall Quiz Results"
  useEffect(() => {
    let filtered = results;
    if (search)
      filtered = filtered.filter((i) =>
        i.fullName?.toLowerCase().includes(search.toLowerCase())
      );
    if (dayFilter !== 'All Days')
      filtered = filtered.filter((i) => i.day?.name === dayFilter);
    setFilteredResults(filtered);
  }, [search, dayFilter, results]);

  // Filter for "Toppers List"
  useEffect(() => {
    let currentToppers = [];

    if (topperTableMode === 'overall') {
      currentToppers = overallToppers;
    } else {
      // ✅ Use same overall-user data for day-wise toppers
      if (topperDayFilter === 'All Days') {
        currentToppers = results;
      } else {
        currentToppers = results.filter(
          (r) => r.day?.name === topperDayFilter
        );
      }
    }

    currentToppers = currentToppers
      .sort(sortByPerformance)
      .filter((i) =>
        i.fullName?.toLowerCase().includes(search.toLowerCase())
      )
      .slice(0, 10);

    setFilteredToppers(currentToppers);
  }, [search, topperDayFilter, overallToppers, topperTableMode, results]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SearchInput
        searchQuery={search}
        handleSearchChange={(e) => setSearch(e.target.value)}
      />

      {/* --- Toppers List (Top 10) --- */}
      <MDTypography variant="h4" mb={3} ml={2} mt={3}>
        Toppers List (Top 10)
      </MDTypography>

      <Box display="flex" gap={2} flexWrap="wrap" px={2} mt={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>View Toppers</InputLabel>
          <Select
            value={topperTableMode}
            onChange={(e) => setTopperTableMode(e.target.value)}
            label="View Toppers"
            style={{ minHeight: 50 }}
          >
            <MenuItem value="overall">Overall Toppers</MenuItem>
            <MenuItem value="dayWise">Day-wise Toppers</MenuItem>
          </Select>
        </FormControl>

        {topperTableMode === 'dayWise' && (
          <FormControl size="small" sx={{ minWidth: 200, ml: 'auto' }}>
            <InputLabel>Filter by Day</InputLabel>
            <Select
              value={topperDayFilter}
              onChange={(e) => setTopperDayFilter(e.target.value)}
              label="Filter by Day"
              style={{ minHeight: 50 }}
            >
              <MenuItem value="All Days">All Days</MenuItem>
              {allDays.map((day, idx) => (
                <MenuItem key={idx} value={day.name}>
                  {day.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <AptaTable
        columns={['Name', 'Day', 'Date', 'Time Taken', 'Total Answers', 'Correct Answers']}
        rows={filteredToppers.map((item, idx) => ({
          Name: <MDTypography variant="caption">{item.fullName}</MDTypography>,
          Day: <MDTypography variant="caption">{item.day?.name || '-'}</MDTypography>,
          Date: (
            <MDTypography variant="caption">
              {item.day?.date ? new Date(item.day.date).toLocaleDateString() : '-'}
            </MDTypography>
          ),
          'Time Taken': <MDTypography variant="caption">{item.timeTaken}</MDTypography>,
          'Total Answers': <MDTypography variant="caption">{item.totalAnswers}</MDTypography>,
          'Correct Answers': <MDTypography variant="caption">{item.correctAnswers}</MDTypography>,
        }))}
      />

      {/* --- Overall Quiz Results --- */}
      <MDTypography variant="h4" mb={3} ml={2} mt={5}>
        Overall Quiz Results
      </MDTypography>

      <Box display="flex" gap={2} flexWrap="wrap" px={2} mt={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 200, ml: 'auto' }}>
          <InputLabel>Filter by Day</InputLabel>
          <Select
            value={dayFilter}
            onChange={(e) => setDayFilter(e.target.value)}
            label="Filter by Day"
            style={{ minHeight: 50 }}
          >
            <MenuItem value="All Days">All Days</MenuItem>
            {allDays.map((day, idx) => (
              <MenuItem key={idx} value={day.name}>
                {day.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <AptaTable
        columns={['Name', 'Day', 'Date', 'Time Taken', 'Total Answers', 'Correct Answers']}
        rows={filteredResults.map((item, idx) => ({
          Name: <MDTypography variant="caption">{item.fullName}</MDTypography>,
          Day: <MDTypography variant="caption">{item.day?.name || '-'}</MDTypography>,
          Date: (
            <MDTypography variant="caption">
              {item.day?.date ? new Date(item.day.date).toLocaleDateString() : '-'}
            </MDTypography>
          ),
          'Time Taken': <MDTypography variant="caption">{item.timeTaken}</MDTypography>,
          'Total Answers': <MDTypography variant="caption">{item.totalAnswers}</MDTypography>,
          'Correct Answers': <MDTypography variant="caption">{item.correctAnswers}</MDTypography>,
        }))}
      />
    </DashboardLayout>
  );
};

export default Results;
